import axiosClient from 'lib/axiosClient';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';

const useOperationProductApis = () => {
  const { enqueueSnackbar } = useSnackbar();

  const request = useCallback(
    (promise, { successMessage } = {}) =>
      promise
        .then(res => {
          if (res.status === 200) {
            if (successMessage) {
              enqueueSnackbar(successMessage, { variant: 'success' });
            }
            return !!res.data?.value ? res.data?.value : res.data;
          }
          enqueueSnackbar(res?.message, { variant: 'error' });
          return null;
        })
        .catch(err => {
          enqueueSnackbar(err?.message || err?.response?.data?.message, {
            variant: 'error',
          });
          return null;
        }),
    [enqueueSnackbar],
  );

  const getOperationProducts = useCallback(
    ({ operationGuid, term = '', page = 1, count = 50 }) =>
      request(
        axiosClient.get('Operation/Product/List', {
          params: {
            Term: term,
            Guid: operationGuid,
            Page: page,
            Count: count,
          },
        }),
      ),
    [request],
  );

  const searchProducts = useCallback(
    ({ zoneGuid, term = '', page = 1, count = 70 }) =>
      request(
        axiosClient.get('ProductSearch/BasicWithPagination', {
          params: {
            Term: term,
            Guid: zoneGuid,
            Page: page,
            Count: count,
          },
        }),
      ),
    [request],
  );

  const getProductPackages = useCallback(
    ({ productGuid, zoneGuid }) =>
      request(
        axiosClient.get('Product/Branch/Packaging/GetAllWithQuantity', {
          params: {
            GUID: productGuid,
            ...(zoneGuid ? { ZoneGuid: zoneGuid } : {}),
          },
        }),
      ),
    [request],
  );

  const insertOperationProduct = useCallback(
    payload =>
      request(
        axiosClient.post('Operation/Product/Insert', {
          operationGuid: payload.operationGuid,
          productPackGuid: payload.productPackGuid,
          quantity: payload.quantity,
        }),
        { successMessage: 'محصول افزوده شد' },
      ),
    [request],
  );

  const updateOperationProductQuantity = useCallback(
    payload =>
      request(
        axiosClient.put('Operation/Product/Update', {
          guid: payload.guid,
          quantity: payload.quantity,
        }),
        { successMessage: 'تعداد بروزرسانی شد' },
      ),
    [request],
  );

  const deleteOperationProduct = useCallback(
    productGuid =>
      request(
        axiosClient.delete('Operation/Product/Delete', {
          params: { Guid: productGuid },
        }),
        { successMessage: 'محصول حذف شد' },
      ),
    [request],
  );

  const getPackUpdateOptions = useCallback(
    operationProductGuid =>
      request(
        axiosClient.get('Operation/Product/Pack/Update', {
          params: { GUID: operationProductGuid },
        }),
      ),
    [request],
  );

  const updateOperationProductPack = useCallback(
    payload =>
      request(
        axiosClient.put('Operation/Product/Pack/Update', {
          operationProductGuid: payload.operationProductGuid,
          packGuid: payload.packGuid,
        }),
        { successMessage: 'بسته بروزرسانی شد' },
      ),
    [request],
  );

  const getProductReports = useCallback(
    operationProductGuid =>
      request(
        axiosClient.get('Operation/Product/Report/List', {
          params: { GUID: operationProductGuid },
        }),
      ),
    [request],
  );

  const insertProductReport = useCallback(
    payload =>
      request(
        axiosClient.post('Operation/Product/Report/Insert', {
          operationProductGuid: payload.operationProductGuid,
          title: payload.title,
          description: payload.description,
          quantity: payload.quantity,
          imageKey: '',
        }),
        { successMessage: 'گزارش ثبت شد' },
      ),
    [request],
  );

  const getProductReport = useCallback(
    reportGuid =>
      request(
        axiosClient.get('Operation/Product/Report/Update', {
          params: { GUID: reportGuid },
        }),
      ),
    [request],
  );

  const updateProductReport = useCallback(
    payload =>
      request(
        axiosClient.put('Operation/Product/Report/Update', {
          guid: payload.guid,
          title: payload.title,
          description: payload.description,
          quantity: payload.quantity,
        }),
        { successMessage: 'گزارش بروزرسانی شد' },
      ),
    [request],
  );

  const deleteProductReport = useCallback(
    reportGuid =>
      request(
        axiosClient.delete('Operation/Product/Report/Delete', {
          params: { GUID: reportGuid },
        }),
        { successMessage: 'گزارش حذف شد' },
      ),
    [request],
  );

  return {
    getOperationProducts,
    searchProducts,
    getProductPackages,
    insertOperationProduct,
    updateOperationProductQuantity,
    deleteOperationProduct,
    getPackUpdateOptions,
    updateOperationProductPack,
    getProductReports,
    insertProductReport,
    getProductReport,
    updateProductReport,
    deleteProductReport,
  };
};

export default useOperationProductApis;
